import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { Session, SupabaseClient } from '@supabase/supabase-js';
import { supabase, supabaseAuth } from './supabase.client';
import { ImpersonationService, ImpersonationTarget } from './impersonation.service';

export type Role = 'Helper' | 'Admin' | 'Service';
export interface RoleClaims { role: Role | null; userId: number | null; displayName: string | null; }

/** The two Supabase clients, injected so tests can substitute fakes (the Angular
 *  unit-test runner cannot mock the module-level `./supabase.client` imports). In
 *  the app they resolve to the real singletons. */
export const AUTH_CLIENT = new InjectionToken<SupabaseClient>('AUTH_CLIENT', {
  providedIn: 'root', factory: () => supabaseAuth,
});
export const DATA_CLIENT = new InjectionToken<SupabaseClient>('DATA_CLIENT', {
  providedIn: 'root', factory: () => supabase,
});

export function decodeRoleClaims(accessToken: string): RoleClaims {
  try {
    const payload = JSON.parse(
      decodeURIComponent(
        atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
          .split('').map(c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join(''),
      ),
    );
    const role = payload.afs_role === 'Helper' || payload.afs_role === 'Admin'
      || payload.afs_role === 'Service'
      ? payload.afs_role as Role : null;
    const userId = typeof payload.afs_user_id === 'number' ? payload.afs_user_id
      : (payload.afs_user_id ? Number(payload.afs_user_id) : null);
    return { role, userId: Number.isFinite(userId as number) ? userId : null,
             displayName: payload.afs_display_name ?? null };
  } catch {
    return { role: null, userId: null, displayName: null };
  }
}

const EMPTY: RoleClaims = { role: null, userId: null, displayName: null };

@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Cloud/Azure client (supabaseAuth) and password/self-hosted data client (supabase). */
  private readonly authClient = inject(AUTH_CLIENT);
  private readonly dataClient = inject(DATA_CLIENT);

  /** Azure/cloud session (authClient) and password/self-hosted session (dataClient). */
  private readonly authSession = signal<Session | null>(null);
  private readonly dataSession = signal<Session | null>(null);

  /** The active session — Azure takes precedence when both exist. */
  readonly session = computed<Session | null>(() => this.authSession() ?? this.dataSession());
  readonly claims = computed<RoleClaims>(() => {
    const token = this.session()?.access_token;
    return token ? decodeRoleClaims(token) : EMPTY;
  });

  private readonly impersonation = inject(ImpersonationService);

  /** Who an Admin session is acting as — null for any other session, so a stale persisted
   *  target on a helper/supervisor device changes nothing (and sends no X-Act-As). */
  readonly actingAs = computed<ImpersonationTarget | null>(() =>
    this.claims().role === 'Admin' ? this.impersonation.target() : null);

  /** The identity the app behaves as: the target while acting as one, else the token's. */
  private readonly effective = computed<RoleClaims>(() => {
    const t = this.actingAs();
    return t ? { role: t.role, userId: t.id, displayName: t.name } : this.claims();
  });

  /** Resolves once both clients' persisted sessions have been restored, so route
   *  guards don't bounce an authenticated user to /login on a cold reload. */
  private resolveReady!: () => void;
  readonly ready = new Promise<void>((resolve) => (this.resolveReady = resolve));

  constructor() {
    Promise.all([
      this.authClient.auth.getSession().then(({ data }) => this.authSession.set(data.session)),
      this.dataClient.auth.getSession().then(({ data }) => this.dataSession.set(data.session)),
    ]).finally(() => this.resolveReady());

    this.authClient.auth.onAuthStateChange((_e, s) => this.authSession.set(s));
    this.dataClient.auth.onAuthStateChange((_e, s) => this.dataSession.set(s));
  }

  /** Password login authenticates against the data/self-hosted project. */
  async signIn(email: string, password: string): Promise<void> {
    const { error } = await this.dataClient.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }

  /** Azure OAuth authenticates against the cloud project (redirects the browser). */
  async signInWithAzure(): Promise<void> {
    const { error } = await this.authClient.auth.signInWithOAuth({
      provider: 'azure',
      options: { scopes: 'openid profile email', redirectTo: window.location.origin },
    });
    if (error) throw error;
  }

  async signOut(): Promise<void> {
    await Promise.all([this.dataClient.auth.signOut(), this.authClient.auth.signOut()]);
    this.authSession.set(null);
    this.dataSession.set(null);
  }

  /** Send a Supabase password-reset email (self-hosted project). */
  async sendPasswordReset(email: string): Promise<void> {
    const { error } = await this.dataClient.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    if (error) throw error;
  }

  /** Returns the active session's access token, refreshing it on demand.
   *  Delegates to the owning client's getSession() (which refreshes an expired
   *  token) rather than reading the cached signal — a suspended iPad tab can
   *  wake with an expired signal value, and there is no 401-retry interceptor. */
  async getAccessToken(): Promise<string | null> {
    await this.ready;
    const client = this.authSession() ? this.authClient
      : this.dataSession() ? this.dataClient
      : null;
    if (!client) return null;
    const { data } = await client.auth.getSession();
    return data.session?.access_token ?? null;
  }

  role(): Role | null { return this.effective().role; }
  userId(): number | null { return this.effective().userId; }
  displayName(): string | null { return this.effective().displayName; }
  /** The signed-in user's own role/id from the token, ignoring impersonation. */
  realRole(): Role | null { return this.claims().role; }
  realUserId(): number | null { return this.claims().userId; }
  isAuthenticated(): boolean { return this.session() !== null && this.role() !== null; }
}
